import React, { useMemo, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Text,
  Button,
  Spinner,
  ProgressBar,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  Add20Regular,
  ArrowRight16Regular,
  Board20Regular,
  TableLink20Regular,
  PlugConnected20Regular,
} from '@fluentui/react-icons';
import { useTopBarTitleNode, useTopBarActions } from '../contexts/TopBarContext';
import { useDashboard } from '../contexts/DashboardContext';
import { useDataModels } from '../contexts/DataModelsContext';
import { useConnections } from '../contexts/ConnectionsContext';
import { useHomeFirstRunState } from '../hooks/useHomeFirstRunState';
import DashboardCard from '../components/dashboard-list/DashboardCard';
import type { DashboardCardItem } from '../components/dashboard-list/DashboardCard';
import DataModelCard from '../components/data-model-list/DataModelCard';
import DataSourceCard from '../components/datasources/DataSourceCard';
import SidePanel from '../components/shared/SidePanel';
import DatasetViewSelector from '../components/dashboard-creation/DatasetViewSelector';
import ConnectionPickerPanel from '../components/data-model-list/ConnectionPickerPanel';
import ConnectorPickerPanel from '../components/datasources/ConnectorPickerPanel';

// First-run components
import FirstRunHero from '../components/home/FirstRunHero';
import FirstRunConnectorGrid from '../components/home/FirstRunConnectorGrid';
import BuildProgressCard from '../components/home/BuildProgressCard';
import SkeletonSection from '../components/home/SkeletonSection';
import CompletionCard from '../components/home/CompletionCard';
import MissingModelHero from '../components/home/MissingModelHero';

// ─── Styles ──────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
  page: {
    maxWidth: '1800px',
    margin: '0 auto',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.padding('16px', '40px', '24px'),
    gap: '32px',
  },
  // First-run page — centered vertically but not full-screen blocking
  firstRunPage: {
    maxWidth: '1800px',
    margin: '0 auto',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    ...shorthands.padding('48px', '40px', '24px'),
    gap: '40px',
    minHeight: 'calc(100vh - 120px)',
  },
  // Progress banner
  banner: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    ...shorthands.padding('10px', '16px'),
    ...shorthands.borderRadius('8px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke3),
    backgroundColor: tokens.colorNeutralBackground3,
  },
  bannerText: {
    flex: 1,
    fontSize: '13px',
    color: tokens.colorNeutralForeground2,
  },
  bannerBold: {
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
  },
  // Section
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  sectionTitle: {
    fontSize: '15px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
  },
  viewAllLink: {
    fontSize: '13px',
    fontWeight: 500,
    color: tokens.colorBrandForeground1,
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    ':hover': {
      textDecoration: 'underline',
    },
  },
  // Card grid — matches original page layouts
  cardGridDashboards: {
    display: 'grid',
    gap: '16px',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
  },
  cardGridModels: {
    display: 'grid',
    gap: '16px',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
  },
  cardGridSources: {
    display: 'grid',
    gap: '16px',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
  },
  // Empty text
  emptyText: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground4,
    paddingLeft: '8px',
  },
  // Loading
  loadingCenter: {
    display: 'flex',
    justifyContent: 'center',
    ...shorthands.padding('64px'),
  },
});

// ─── Component ───────────────────────────────────────────────────────────────

const OverviewPage: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();

  const { dashboards, isLoading: dashboardsLoading, duplicateDashboard, deleteDashboard, createDashboardWithView } = useDashboard();
  const { dataModels, isLoading: modelsLoading } = useDataModels();
  const { connections, loading: connectionsLoading } = useConnections();

  const [pickerOpen, setPickerOpen] = useState(false);
  const [modelPanelOpen, setModelPanelOpen] = useState(false);
  const [sourcePanelOpen, setSourcePanelOpen] = useState(false);

  // First-run state
  const firstRunState = useHomeFirstRunState();
  const [completionDismissed, setCompletionDismissed] = useState(false);

  const isLoading = dashboardsLoading || modelsLoading || connectionsLoading;

  // ── TopBar title ────────────────────────────────────────────────────────

  const subtitle = useMemo(() => {
    if (firstRunState.isFirstRunEmpty) return '';
    const parts: string[] = [];
    parts.push(`${dashboards.length} dashboard${dashboards.length !== 1 ? 's' : ''}`);
    parts.push(`${dataModels.length} model${dataModels.length !== 1 ? 's' : ''}`);
    parts.push(`${connections.length} source${connections.length !== 1 ? 's' : ''}`);
    return parts.join(' • ');
  }, [dashboards.length, dataModels.length, connections.length, firstRunState.isFirstRunEmpty]);

  const titleNode = useMemo(() => (
    <span style={{ display: 'flex', alignItems: 'baseline', gap: '12px' }}>
      <span style={{ fontWeight: 600, fontSize: '20px' }}>Home</span>
      {subtitle && (
        <span style={{ fontSize: '13px', fontWeight: 400, color: tokens.colorNeutralForeground3 }}>
          {subtitle}
        </span>
      )}
    </span>
  ), [subtitle]);

  useTopBarTitleNode(titleNode);

  // ── TopBar actions: + Create dropdown ───────────────────────────────────

  const topBarActions = useMemo(() => {
    // Hide create menu for first-run empty state
    if (firstRunState.isFirstRunEmpty) return null;

    return (
      <Menu>
        <MenuTrigger disableButtonEnhancement>
          <Button appearance="primary" icon={<Add20Regular />}>Create</Button>
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            <MenuItem icon={<Board20Regular />} onClick={() => setPickerOpen(true)}>
              Create Dashboard
            </MenuItem>
            <MenuItem icon={<TableLink20Regular />} onClick={() => setModelPanelOpen(true)}>
              Create Data Model
            </MenuItem>
            <MenuItem icon={<PlugConnected20Regular />} onClick={() => setSourcePanelOpen(true)}>
              Connect Data Source
            </MenuItem>
          </MenuList>
        </MenuPopover>
      </Menu>
    );
  }, [firstRunState.isFirstRunEmpty]);

  useTopBarActions(topBarActions);

  // ── Processing state detection ──────────────────────────────────────────

  const activeSource = useMemo(() => {
    const inProgressStatuses = ['syncing', 'draft', 'authenticating', 'configuring'];
    return connections.find(c => inProgressStatuses.includes(c.status)) ?? null;
  }, [connections]);

  const activeModel = useMemo(() => {
    return dataModels.find(m => m.status === 'building') ?? null;
  }, [dataModels]);

  const isSetupInProgress = Boolean(activeSource || activeModel);

  const getProgressLabel = (): string => {
    const srcStatus = activeSource?.status as string | undefined;
    if (srcStatus === 'syncing') return 'Importing data';
    if (srcStatus === 'draft' || srcStatus === 'authenticating' || srcStatus === 'configuring') return 'Preparing connection';
    if (activeModel?.status === 'building') return 'Building model';
    return 'Processing';
  };

  // ── Data ────────────────────────────────────────────────────────────────

  const recentDashboards: DashboardCardItem[] = useMemo(() => {
    return [...dashboards]
      .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
      .slice(0, 4)
      .map(d => ({
        id: d.id,
        name: d.name,
        description: d.description,
        status: d.status,
        createdAt: d.createdAt,
        updatedAt: d.updatedAt,
        chartCount: d.chartCount ?? 0,
        dataSourceName: d.dataSourceName,
        connectorId: d.connectorId,
        questions: d.questions,
        thumbnailUrl: d.thumbnailUrl,
      }));
  }, [dashboards]);

  const recentModels = useMemo(() => {
    return [...dataModels]
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
      .slice(0, 4);
  }, [dataModels]);

  const recentSources = useMemo(() => {
    return [...connections]
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
      .slice(0, 4);
  }, [connections]);

  // ── Handlers ────────────────────────────────────────────────────────────

  const handleDashboardDuplicate = useCallback((id: string) => { duplicateDashboard(id); }, [duplicateDashboard]);
  const handleDashboardDelete = useCallback(async (id: string) => { await deleteDashboard(id); }, [deleteDashboard]);

  const handleViewSelect = useCallback(
    async (datasetViewId: string, _viewName: string) => {
      try {
        const newDashboard = await createDashboardWithView(datasetViewId);
        if (newDashboard && newDashboard.id) {
          setPickerOpen(false);
          navigate(`/dashboards/${newDashboard.id}`, {
            replace: true,
            state: { generating: true, jobId: newDashboard.jobId },
          });
        }
      } catch (error) {
        console.error('Failed to create dashboard:', error);
      }
    },
    [createDashboardWithView, navigate],
  );

  // First-run CTA handlers
  const handleConnectSource = useCallback(() => {
    setSourcePanelOpen(true);
  }, []);

  const handleImportFile = useCallback(() => {
    // Navigate directly to the unified file upload page (CSV + Excel)
    navigate('/data-sources/new/connect?connector=file');
  }, [navigate]);

  // ── Render ──────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className={styles.page}>
        <div className={styles.loadingCenter}>
          <Spinner size="large" label="Loading workspace..." />
        </div>
      </div>
    );
  }

  // ─── First-run empty state ──────────────────────────────────────────────
  if (firstRunState.isFirstRunEmpty) {
    return (
      <>
        <div className={styles.firstRunPage}>
          <FirstRunHero
            onConnectSource={handleConnectSource}
            onImportFile={handleImportFile}
          />
          <FirstRunConnectorGrid />
        </div>

        {/* Side Panels (same as normal view) */}
        <ConnectorPickerPanel isOpen={sourcePanelOpen} onClose={() => setSourcePanelOpen(false)} />
      </>
    );
  }

  // ─── Source exists, model missing — recovery state ──────────────────────
  if (firstRunState.workspaceState === 'missing_model') {
    return (
      <div className={styles.page}>
        <MissingModelHero
          onGenerateModel={() => setModelPanelOpen(true)}
          onViewSource={() => {
            const src = connections[0];
            if (src) navigate(`/data-sources/${src.id}`);
          }}
        />

        {/* Sources section */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <Text className={styles.sectionTitle}>Sources</Text>
            <Link to="/data-sources" className={styles.viewAllLink}>
              View All <ArrowRight16Regular />
            </Link>
          </div>
          <div className={styles.cardGridSources}>
            {recentSources.map(item => (
              <DataSourceCard
                key={item.id}
                item={item}
                onClick={() => navigate(`/data-sources/${item.id}`)}
              />
            ))}
          </div>
        </div>

        {/* Side Panels */}
        <ConnectionPickerPanel isOpen={modelPanelOpen} onClose={() => setModelPanelOpen(false)} />
        <ConnectorPickerPanel isOpen={sourcePanelOpen} onClose={() => setSourcePanelOpen(false)} />
      </div>
    );
  }

  // ─── First build in progress ────────────────────────────────────────────
  if (firstRunState.isFirstBuildInProgress) {
    return (
      <div className={styles.page}>
        {/* Pinned progress card */}
        <BuildProgressCard
          connectorId={firstRunState.activeConnectorId}
        />

        {/* Skeleton sections while building */}
        <SkeletonSection variant="dashboards" />
        <SkeletonSection variant="insights" />
        <SkeletonSection variant="datasets" />

        {/* Side Panels */}
        <SidePanel isOpen={pickerOpen} onClose={() => setPickerOpen(false)} title="Create Dashboard" width={400}>
          <DatasetViewSelector onSelect={handleViewSelect} />
        </SidePanel>
        <ConnectionPickerPanel isOpen={modelPanelOpen} onClose={() => setModelPanelOpen(false)} />
        <ConnectorPickerPanel isOpen={sourcePanelOpen} onClose={() => setSourcePanelOpen(false)} />
      </div>
    );
  }

  // ─── Normal Home (with optional completion card) ────────────────────────
  return (
    <div className={styles.page}>
      {/* ── Completion card (first-build-complete, dismissible) ──────── */}
      {firstRunState.isFirstBuildComplete && !completionDismissed && (
        <CompletionCard onDismiss={() => setCompletionDismissed(true)} />
      )}


      {/* ── Progress banner (non-first-run active jobs) ───────────── */}
      {isSetupInProgress && !firstRunState.isFirstBuildComplete && (
        <div className={styles.banner}>
          <ProgressBar thickness="large" style={{ width: '80px' }} />
          <Text className={styles.bannerText}>
            <span className={styles.bannerBold}>Workspace setup in progress</span>
            {' · '}
            {activeSource?.name ?? activeModel?.name ?? 'Processing'}
            {' · '}
            {getProgressLabel()}
          </Text>
          <Button
            appearance="subtle"
            size="small"
            onClick={() => {
              if (activeSource) navigate(`/data-sources/${activeSource.id}`);
              else if (activeModel) navigate(`/data-models/${activeModel.id}`);
            }}
          >
            View Progress
          </Button>
        </div>
      )}

      {/* ── Recent Dashboards (hidden when empty) ─────────────────── */}
      {recentDashboards.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <Text className={styles.sectionTitle}>Recent Dashboards</Text>
            <Link to="/dashboards" className={styles.viewAllLink}>
              View All <ArrowRight16Regular />
            </Link>
          </div>
          <div className={styles.cardGridDashboards}>
            {recentDashboards.map(item => (
              <DashboardCard
                key={item.id}
                item={item}
                onClick={() => navigate(`/dashboards/${item.id}`)}
                onDuplicate={handleDashboardDuplicate}
                onDelete={handleDashboardDelete}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── Continue Working / Models ─────────────────────────────── */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <Text className={styles.sectionTitle}>
            {!firstRunState.hasDashboards ? 'Continue Working' : 'Models'}
          </Text>
          {dataModels.length > 1 && (
            <Link to="/data-models" className={styles.viewAllLink}>
              View All <ArrowRight16Regular />
            </Link>
          )}
        </div>
        <div className={styles.cardGridModels}>
          {(!firstRunState.hasDashboards ? recentModels.slice(0, 1) : recentModels).map(item => (
            <DataModelCard
              key={item.id}
              item={item}
              onClick={() => navigate(`/data-models/${item.id}`)}
            />
          ))}
          {recentModels.length === 0 && (
            <Text className={styles.emptyText}>No models yet</Text>
          )}
        </div>
      </div>

      {/* ── Sources ────────────────────────────────────────────────── */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <Text className={styles.sectionTitle}>Sources</Text>
          {connections.length > 0 && (
            <Link to="/data-sources" className={styles.viewAllLink}>
              View All <ArrowRight16Regular />
            </Link>
          )}
        </div>
        <div className={styles.cardGridSources}>
          {recentSources.length > 0 ? (
            recentSources.map(item => (
              <DataSourceCard
                key={item.id}
                item={item}
                onClick={() => navigate(`/data-sources/${item.id}`)}
              />
            ))
          ) : (
            <Text className={styles.emptyText}>No sources yet</Text>
          )}
        </div>
      </div>

      {/* ── Side Panels ────────────────────────────────────────────── */}
      <SidePanel isOpen={pickerOpen} onClose={() => setPickerOpen(false)} title="Create Dashboard" width={400}>
        <DatasetViewSelector onSelect={handleViewSelect} />
      </SidePanel>
      <ConnectionPickerPanel isOpen={modelPanelOpen} onClose={() => setModelPanelOpen(false)} />
      <ConnectorPickerPanel isOpen={sourcePanelOpen} onClose={() => setSourcePanelOpen(false)} />
    </div>
  );
};

export default OverviewPage;